import React, { useState, useCallback, useEffect } from 'react'
import { css } from '@emotion/react'
import Announcement from '@groovehq/internal-design-system/lib/components/Announcement/Announcement'
import { styles } from '@groovehq/internal-design-system/lib/components/Announcement/Announcement.styles'
import { heading } from '@groovehq/internal-design-system/lib/styles/elements'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { doSetFlag } from 'ducks/flags/operations'
import { selectFlag, selectIsFetchingFlags } from 'ducks/flags/selectors'
import { selectIsKbMigrationBannerShowing } from 'subapps/kb/selectors/knowledge_bases'
import {
  HIDE_HELPLY_PRODUCT_HUNT_ANNOUNCEMENT,
  HIDE_HELPLY_PRODUCT_HUNT_BANNER,
} from 'ducks/flags/flagNames'
import { selectIsTestAccount } from 'selectors/account'

export const HELPLY_PRODUCT_HUNT_URL =
  'https://www.producthunt.com/products/helply'

const logoStyle = css`
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  display: block;
`

const copyStyle = css`
  max-width: 400px;
`

const BANNER_DISMISSED_TIME_KEY = 'helply_product_hunt_banner_dismissed_at'
const POPUP_DELAY_MS = 24 * 60 * 60 * 1000 // 1 day in milliseconds

const getTimeSinceBannerDismissed = () => {
  const dismissedAt = localStorage.getItem(BANNER_DISMISSED_TIME_KEY)
  if (!dismissedAt) return null
  return Date.now() - parseInt(dismissedAt, 10)
}

const InboxAnnouncement = () => {
  const dispatch = useDispatch()

  const fetching = useSelector(selectIsFetchingFlags)
  const announcementFlag = useSelector(state =>
    selectFlag(state)(HIDE_HELPLY_PRODUCT_HUNT_ANNOUNCEMENT)
  )
  const isKbMigrationBannerShowing = useSelector(
    selectIsKbMigrationBannerShowing
  )
  const isBannerHidden = useSelector(state =>
    selectFlag(state)(HIDE_HELPLY_PRODUCT_HUNT_BANNER)
  )
  const bannerFlag = isBannerHidden || !!isKbMigrationBannerShowing

  const isTestAccount = useSelector(selectIsTestAccount)

  // Store timestamp when banner is dismissed
  // Only show popup after 1 day has passed
  useEffect(
    () => {
      if (!fetching && bannerFlag === true) {
        const existingTimestamp = localStorage.getItem(
          BANNER_DISMISSED_TIME_KEY
        )
        if (!existingTimestamp) {
          // Banner was just dismissed, store the timestamp
          localStorage.setItem(BANNER_DISMISSED_TIME_KEY, Date.now().toString())
        }
      }
    },
    [fetching, bannerFlag]
  )

  const timeSinceDismissed = getTimeSinceBannerDismissed()
  const hasWaitedLongEnough =
    (timeSinceDismissed !== null && timeSinceDismissed >= POPUP_DELAY_MS) ||
    isKbMigrationBannerShowing

  const shouldHideAnnouncement =
    isTestAccount ||
    fetching ||
    announcementFlag ||
    bannerFlag !== true ||
    !hasWaitedLongEnough

  const [show, setShow] = useState(true)
  const handleCloseAnnouncement = useCallback(
    () => {
      dispatch(doSetFlag(HIDE_HELPLY_PRODUCT_HUNT_ANNOUNCEMENT))
      if (!isBannerHidden) {
        dispatch(doSetFlag(HIDE_HELPLY_PRODUCT_HUNT_BANNER))
      }
      setShow(false)
    },
    [dispatch, isBannerHidden]
  )

  if (shouldHideAnnouncement !== false) return null
  return (
    <Announcement.Modal
      open={show}
      portal="#overlays"
      onClose={handleCloseAnnouncement}
    >
      <div className="grui text-center mt-8">
        <svg
          css={logoStyle}
          viewBox="0 0 240 240"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="120" cy="120" r="120" fill="#FF6154" />
          <path
            d="M133.333 120H106.667V93.3333H133.333C140.693 93.3333 146.667 99.3067 146.667 106.667C146.667 114.027 140.693 120 133.333 120Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M133.333 80H93.3333V160H106.667V133.333H133.333C148.061 133.333 160 121.394 160 106.667C160 91.9391 148.061 80 133.333 80Z"
            fill="white"
          />
        </svg>
        <h2 css={heading.styles.h2} className="grui mt-5">
          We&apos;re on Product Hunt!
        </h2>
        <p css={[styles.bigCopy, copyStyle]}>
          {`We just launched our sister company, Helply!`}
          <br />
          {`If you love Groove, please help us spread the word and support us on Product Hunt.`}
        </p>
        <Button
          as="a"
          href={HELPLY_PRODUCT_HUNT_URL}
          target="_blank"
          rel="noopener noreferrer"
          type="info"
          size="xBig"
          css={styles.btn}
          className="grui mr-5"
          onClick={handleCloseAnnouncement}
        >
          Support us on Product Hunt
        </Button>
      </div>
    </Announcement.Modal>
  )
}

export default InboxAnnouncement
